/* eslint-disable @typescript-eslint/no-floating-promises */
import { AddIcon } from "@chakra-ui/icons"
import { Box, Button, Modal, ModalCloseButton, ModalContent, ModalOverlay, Text, useDisclosure } from "@chakra-ui/react"
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useAtom, useAtomValue, useSetAtom } from "jotai"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FMPTableType, Paths } from "../../../common/commonConstants"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { Loading } from "../../../components/commons/loader.main"
import { Colors } from "../../../theme/colors"
import { useViewFmpProjectLazyQuery } from "../../../type-and-hooks"
import { editFmpRowOrderSnapshotAtom } from "../common.fmp.hooks/editFmpDirty.store"
import EditIndividualRow from "../editIndividualRow/editIndividualRow"
import { isReorderModeAtom, sortedOrderAtom } from "./fmpSortedOrder.store"
import { FMPProjectRowItem } from "./fmpprojectRowItem"

interface FMPProjectTableProps {
  fmpId: number | undefined
  tableType?: FMPTableType
  onRefetchAvailable?: (refetchFn: () => void) => void
}
export interface EditColumnsInterface {
  headingLabel: string
  dataKey: string
}

export interface ViewColumnsInterface {
  headingLabel: string
  dataKey: string
  accessorFn?: (row: any) => any
  isSortable?: boolean
  isNumeric?: boolean
}

// Columns whose header and row values should be centre-aligned. View FMP and
// Edit FMP share the exact same dataKeys for these fields, so this applies
// consistently to both table types (see fmpprojectRowItem.tsx for the row
// side of this).
export const CENTERED_COLUMN_KEYS = ["rowIndex", "barrier_type", "system_frl", "application", "fire_stopping_system"]

const FMPProjectTable: React.FC<FMPProjectTableProps> = ({ fmpId, tableType = FMPTableType.VIEW_FMP, onRefetchAvailable }) => {
  const [fetchFMPProject, { data: fmpProjectData, loading: fmpProjectLoading, refetch }] = useViewFmpProjectLazyQuery({
    fetchPolicy: "no-cache"
  })
  const [userDetails] = useAtom(userDetailsAtom)
  const [isReorderMode] = useAtom(isReorderModeAtom)
  const [currentProjectId, setCurrentProjectId] = useState<string>()
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )
  const navigate = useNavigate()
  const { isOpen, onClose, onOpen } = useDisclosure()
  const [getSortedOrderAtom, setSortedOrderAtom] = useAtom(sortedOrderAtom)
  const setRowOrderSnapshot = useSetAtom(editFmpRowOrderSnapshotAtom)
  const rowOrderSnapshot = useAtomValue(editFmpRowOrderSnapshotAtom)
  const getIsEditOrDelete = (item: string | undefined): boolean => item === "Edit" || item === "Delete"

  const getRedColorIfEditOrDelete = (item: string | undefined): string =>
    item === "Edit" || item === "Delete" ? Colors.Primary.c1 : Colors.Alternative.accent

  const editColumns = useMemo(
    () => [
      {
        headingLabel: "No",
        dataKey: "rowIndex"
      },
      {
        headingLabel: "Project System Ref.",
        dataKey: "project_system_ref"
      },
      {
        headingLabel: "Barrier Type",
        dataKey: "barrier_type"
      },
      {
        headingLabel: "Barrier Construction",
        dataKey: "barrier_construction"
      },

      {
        headingLabel: "System FRL",
        dataKey: "system_frl"
      },
      {
        headingLabel: "Services Penetrating",
        dataKey: "fmp_product_services"
      },
      {
        headingLabel: "Application",
        dataKey: "application"
      },
      {
        headingLabel: "Fire Stopping System",
        dataKey: "fire_stopping_system"
      },
      {
        headingLabel: "Report Reference",
        dataKey: "report_reference"
      },
      {
        headingLabel: "Report Date",
        dataKey: "report_date"
      }
      // ,{
      //   headingLabel: 'Report Expiry Date',
      //   dataKey: 'report_expiry_date'
      // }
    ],
    []
  )

  const viewColumns = useMemo(
    () => [
      {
        headingLabel: "No",
        dataKey: "rowIndex"
      },
      {
        headingLabel: "Project System Ref.",
        dataKey: "project_system_ref"
      },
      {
        headingLabel: "Barrier Type",
        dataKey: "barrier_type"
      },
      {
        headingLabel: "Barrier Construction",
        dataKey: "barrier_construction"
      },

      {
        headingLabel: "System FRL",
        dataKey: "system_frl"
      },
      {
        headingLabel: "Services Penetrating",
        dataKey: "fmp_product_services"
      },
      {
        headingLabel: "Application",
        dataKey: "application"
      },
      {
        headingLabel: "Fire Stopping System",
        dataKey: "fire_stopping_system"
      },
      {
        headingLabel: "Report Reference",
        dataKey: "report_reference"
      },
      {
        headingLabel: "Report Date",
        dataKey: "report_date"
      }
    ],
    []
  )

  const columns = useMemo(() => {
    return tableType === FMPTableType.Edit_FMP ? editColumns : viewColumns
  }, [editColumns, tableType, viewColumns])

  const templateColumns = useMemo(() => {
    const baseColumns = columns.length
    const additionalColumns = tableType === FMPTableType.Edit_FMP ? 2 : 0
    // return `repeat(${baseColumns + additionalColumns}, 1fr)`
    return tableType === FMPTableType.Edit_FMP
      ? "3% 15% 6% 12% 8% 10% 8% 8% 8% 12% 5% 5%"
      : "3% 15% 8% 16% 8% 10% 8% 8% 12% 12%"
  }, [columns.length, tableType])

  const getColumnCount = useCallback((): number => {
    return columns.length + (tableType === FMPTableType.Edit_FMP ? 2 : 0)
  }, [columns.length, tableType])

  const navigateToProductFilter = useCallback((): void => {
    navigate(Paths.FMP.PRODUCT_FILTER, { state: { fmpId } })
  }, [navigate, fmpId])

  const handleEditOrDelete = (label: string, row: string): void => {
    if (label === "delete") {
      // Delete functionality
    }
    if (label === "edit") {
      setCurrentProjectId(row)
      onOpen()
    }
  }

  const handleProductDeleteSuccess = (): void => {
    refetch()
  }

  useEffect(() => {
    const userId = userDetails?._id
    if (userId && fmpId) {
      fetchFMPProject({ variables: { fmpProjectId: fmpId, userId: Number(userId) } })
    }
  }, [fmpId, fetchFMPProject, userDetails])

  // Expose refetch function to parent component - only once when component mounts
  useEffect(() => {
    if (onRefetchAvailable && refetch) {
      onRefetchAvailable(() => {
        void refetch()
      })
    }
  }, [onRefetchAvailable]) // Remove refetch from dependencies to prevent infinite loop

  useEffect(() => {
    if (fmpProjectData?.viewFMPProject) {
      const sortedList = fmpProjectData.viewFMPProject.map((item: any, index: number) => ({
        ...item,
        rowIndex: index + 1
      }))
      setSortedOrderAtom(sortedList)
      if (tableType === FMPTableType.Edit_FMP) {
        setRowOrderSnapshot(sortedList.map((item: any) => (item?.id !== undefined && item?.id !== null ? String(item.id) : "")))
      }
    } else {
      setSortedOrderAtom([])
    }
  }, [fmpProjectData, setSortedOrderAtom, tableType, setRowOrderSnapshot])

  // While reorder mode is active, a row whose id no longer sits at its
  // last-saved position is "out of order" and gets flagged amber instead
  // of the default green (see FMPProjectRowItem's "No" column).
  const isRowOutOfOrder = useCallback(
    (row: any, index: number): boolean => {
      if (!rowOrderSnapshot) return false
      return rowOrderSnapshot[index] !== String(row?.id ?? "")
    },
    [rowOrderSnapshot]
  )

  const handleDragEnd = (event: DragEndEvent): void => {
    const { active, over } = event

    // Add comprehensive null checks
    if (!active?.id || !over?.id) return
    if (active.id === over.id) return

    // Ensure getSortedOrderAtom is not null/undefined
    if (!getSortedOrderAtom || getSortedOrderAtom.length === 0) return

    const oldIndex = getSortedOrderAtom.findIndex(item => item?.id === active.id)
    const newIndex = getSortedOrderAtom.findIndex(item => item?.id === over.id)

    // Check if indices are valid
    if (oldIndex === -1 || newIndex === -1) return

    const sortedOrder = arrayMove(getSortedOrderAtom, oldIndex, newIndex)
    setSortedOrderAtom(sortedOrder)
  }

  // Add null check for getSortedOrderAtom before rendering
  const hasValidData = getSortedOrderAtom && getSortedOrderAtom.length > 0

  // Determine if drag and drop should be enabled
  const isDragEnabled = tableType === FMPTableType.Edit_FMP && isReorderMode
  const isViewFMP = tableType === FMPTableType.VIEW_FMP

  return (
    <>
      <Box>
        <Loading loading={fmpProjectLoading} />

        <Box display="grid" gridTemplateColumns={templateColumns} mt="2" w="100%">
          {/* Headers */}
          {columns.map(({ headingLabel, dataKey }, index) => (
            <Box
              key={index}
              p="2"
              bg={isViewFMP ? Colors.Alternative.accent : getRedColorIfEditOrDelete(headingLabel)}
              color="white"
              fontWeight="normal"
              borderRight={`1px solid ${Colors.Secondary.c2_500}`}
              alignContent={getIsEditOrDelete(headingLabel) ? "center" : "flex-end"}
              textAlign={CENTERED_COLUMN_KEYS.includes(dataKey) ? "center" : "left"}
            >
              <Text variant="RCaption">{headingLabel}</Text>
            </Box>
          ))}

          {tableType === FMPTableType.Edit_FMP && (
            <>
              <Box
                p="2"
                bg={getRedColorIfEditOrDelete("Edit")}
                color="white"
                fontWeight="normal"
                borderRight={`1px solid ${getIsEditOrDelete("Edit")}`}
                alignContent={getIsEditOrDelete("Edit") ? "center" : "flex-end"}
              >
                <Text variant="RCaption" textAlign={getIsEditOrDelete("Delete") ? "center" : "start"}>
                  Edit
                </Text>
              </Box>
              <Box
                p="2"
                bg={getRedColorIfEditOrDelete("Delete")}
                color="white"
                fontWeight="normal"
                borderRight={`1px solid ${Colors.Secondary.c2_500}`}
                alignContent={getIsEditOrDelete("Delete") ? "center" : "flex-end"}
              >
                <Text variant="RCaption" textAlign={getIsEditOrDelete("Delete") ? "center" : "start"}>
                  Delete
                </Text>
              </Box>
            </>
          )}

          {/* Rows */}
          {hasValidData && (
            <>
              {isDragEnabled && (
                // Edit mode with reorder enabled - with drag and drop
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={getSortedOrderAtom} strategy={verticalListSortingStrategy}>
                    {fmpId &&
                      getSortedOrderAtom?.map((row: any, index: number) => {
                        // Add null check for row
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        if (!row || !row.id) {
                          console.warn("Invalid row data:", row)
                          return null
                        }

                        return (
                          <FMPProjectRowItem
                            columnCount={getColumnCount()}
                            templateColumns={templateColumns}
                            key={`table-row-${row.id}`}
                            fmpId={fmpId}
                            index={index}
                            columns={columns}
                            row={row}
                            tableType={tableType}
                            editOrDeleteCallback={handleEditOrDelete}
                            onDeleteSuccessCallBack={handleProductDeleteSuccess}
                            isReorderMode={!isViewFMP && isReorderMode}
                            isRowOutOfOrder={isRowOutOfOrder(row, index)}
                          />
                        )
                      })}
                  </SortableContext>
                </DndContext>
              )}
              {!isDragEnabled && (
                // View mode or edit mode without reorder - no drag and drop
                <>
                  {fmpId &&
                    getSortedOrderAtom?.map((row: any, index: number) => {
                      // Add null check for row
                      // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                      if (!row || !row.id) {
                        console.warn("Invalid row data:", row)
                        return null
                      }

                      return (
                        <FMPProjectRowItem
                          columnCount={getColumnCount()}
                          templateColumns={templateColumns}
                          key={`table-row-${row.id}`}
                          fmpId={fmpId}
                          index={index}
                          columns={columns}
                          row={row}
                          tableType={tableType}
                          editOrDeleteCallback={handleEditOrDelete}
                          onDeleteSuccessCallBack={handleProductDeleteSuccess}
                          isReorderMode={!isViewFMP && isReorderMode}
                          isRowOutOfOrder={isRowOutOfOrder(row, index)}
                        />
                      )
                    })}
                </>
              )}
            </>
          )}

          {!isViewFMP && (
            <Button bg="primary" color="white" borderRadius="0" p="0" _hover={{ bg: "primary" }} onClick={navigateToProductFilter}>
              <AddIcon fontSize="11px" />
            </Button>
          )}
        </Box>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent px="40px" py="24px" bg="accent" color="white" maxW="760px">
          <ModalCloseButton h="24px" />
          <EditIndividualRow
            onClose={onClose}
            fmpId={fmpId}
            fmpProductId={currentProjectId}
            onUpdateSuccess={() => {
              refetch()
            }}
          />
        </ModalContent>
      </Modal>
    </>
  )
}

export default FMPProjectTable
